import { ref, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getItem, removeItem, setItem } from '@/utils/storage'
import { paramValue, queryToTokens, sameTokens, tokensToQuery } from './tokens'
import type { FilterSchema, FilterToken, LabelMap } from './types'

/**
 * `utils/storage` defaults to a 120 second TTL, which turns a preference into a cache. The task
 * filters already use a week; the same here.
 */
export const STORAGE_TTL_SECONDS = 60 * 60 * 24 * 7

export function filterStorageKey(resource: string): string {
  return 'filter-bar.' + resource
}

/**
 * Keeps the filter state, the URL and the local mirror in sync.
 *
 * Two rules decide who wins, and both matter for links people share:
 *
 * 1. If the URL carries any filter parameter, the URL wins and is mirrored into storage. A link
 *    someone sends therefore shows the recipient the same list as the sender, instead of silently
 *    being overwritten by whatever the recipient filtered last.
 * 2. If the URL carries none, the last state is restored from storage and written into the URL.
 *    Coming back to the list keeps the filter, and it is visible in the address bar rather than
 *    being invisible state, which is what the old contacts filter card did.
 *
 * Writes use `router.replace`. The existing `utils/url.js#addQueryParam` pushes, so every keystroke
 * in a filter used to add a history entry and the back button walked backwards through them.
 */
export function useFilterUrl(
  schema: FilterSchema,
  labels: Ref<LabelMap>,
  onUrlChange?: () => void,
) {
  const route = useRoute()
  const router = useRouter()
  // The list this filter belongs to. Leaving it changes the query too, and that is no filter change.
  const path = route.path

  const tokens = ref<FilterToken[]>([])

  function urlCarriesFilters(): boolean {
    return schema.definitions.some((definition) => {
      const value = paramValue(route.query as Record<string, unknown>, definition)
      return value !== undefined && value !== null && value !== ''
    })
  }

  /** A former parameter name in the URL, e.g. a bookmark from before the space rename. */
  function urlCarriesLegacyParams(): boolean {
    return schema.definitions.some((definition) =>
      (definition.legacyParams ?? []).some((legacy) => route.query[legacy] !== undefined),
    )
  }

  function persist(): void {
    const query = tokensToQuery(schema, tokens.value)
    if (Object.keys(query).length) {
      setItem(filterStorageKey(schema.resource), query, STORAGE_TTL_SECONDS)
    } else {
      removeItem(filterStorageKey(schema.resource))
    }
  }

  function writeUrl(): void {
    const query: Record<string, unknown> = { ...route.query }
    schema.definitions.forEach((definition) => {
      delete query[definition.param]
      for (const legacy of definition.legacyParams ?? []) {
        delete query[legacy]
      }
    })
    Object.assign(query, tokensToQuery(schema, tokens.value))
    // A narrower filter can leave the current page out of range, so paging starts over.
    delete query.page
    router.replace({ query: query as Record<string, string> }).catch(() => {
      // A redundant navigation rejects; the state is already what we wanted.
    })
  }

  function relabel(list: FilterToken[]): FilterToken[] {
    return list.map((token) => ({
      ...token,
      label: labels.value[token.key]?.[token.value] ?? token.label,
    }))
  }

  function setTokens(next: FilterToken[]): void {
    tokens.value = relabel(next)
    persist()
    writeUrl()
  }

  /**
   * Called once the consumer has mounted and its option labels are on their way.
   *
   * A third, weakest source comes after URL and storage: the schema's `fallbackQuery`, e.g. the
   * current space (#154). It is shown and written into the URL but not stored, so a later change
   * of the space is picked up again.
   */
  async function restore(): Promise<void> {
    if (urlCarriesFilters()) {
      tokens.value = queryToTokens(schema, route.query as Record<string, unknown>, labels.value)
      persist()
      // An old link shows the current parameter names from now on.
      if (urlCarriesLegacyParams()) writeUrl()
      return
    }

    const stored = getItem(filterStorageKey(schema.resource))
    if (stored && typeof stored === 'object') {
      const restored = queryToTokens(schema, stored as Record<string, unknown>, labels.value)
      if (restored.length) {
        tokens.value = restored
        // Rewrites a filter stored under a former parameter name.
        persist()
        writeUrl()
        return
      }
    }

    const fallback = schema.fallbackQuery ? await schema.fallbackQuery() : null
    if (fallback) {
      const restored = queryToTokens(schema, fallback, labels.value)
      if (restored.length) {
        tokens.value = restored
        writeUrl()
      }
    }
  }

  // Options arrive asynchronously, so a token restored from a URL starts out labelled with its raw
  // id and is renamed once the list is known.
  watch(labels, () => {
    tokens.value = relabel(tokens.value)
  })

  // Back, forward, a saved view opened as a link, or the space switcher: adopt the URL without
  // writing it again, and tell the consumer, because the list has to load for the new filter.
  // Our own writes come back here with the same tokens and are skipped.
  watch(
    () => route.query,
    (query) => {
      if (route.path !== path) return
      const fromUrl = queryToTokens(schema, query as Record<string, unknown>, labels.value)
      if (!sameTokens(schema, fromUrl, tokens.value)) {
        tokens.value = fromUrl
        persist()
        onUrlChange?.()
      }
    },
  )

  return { tokens, setTokens, restore }
}
